import { createContext, useContext } from "react";

export type DiscoverGamesBulkSelectContextValue = {
	selectedIds: ReadonlySet<number>;
	isSelected: (id: number) => boolean;
	toggle: (id: number) => void;
	setMany: (ids: number[], selected: boolean) => void;
	clear: () => void;
};

export type DiscoverGamesBulkSelectStore = {
	subscribe: (listener: () => void) => () => void;
	getSelectedIds: () => ReadonlySet<number>;
	isSelected: (id: number) => boolean;
	toggle: (id: number) => void;
	setMany: (ids: number[], selected: boolean) => void;
	clear: () => void;
};

/** Stable empty snapshot for SSR — must not be mutated. */
export const EMPTY_SELECTED_GAME_IDS: ReadonlySet<number> = new Set<number>();

export function createDiscoverGamesBulkSelectStore(): DiscoverGamesBulkSelectStore {
	let selectedIds = new Set<number>();
	const listeners = new Set<() => void>();

	const notify = () => {
		for (const listener of listeners) {
			listener();
		}
	};

	return {
		subscribe(listener) {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
		getSelectedIds() {
			return selectedIds;
		},
		isSelected(id) {
			return selectedIds.has(id);
		},
		toggle(id) {
			const next = new Set(selectedIds);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			selectedIds = next;
			notify();
		},
		setMany(ids, selected) {
			const next = new Set(selectedIds);
			for (const id of ids) {
				if (selected) {
					next.add(id);
				} else {
					next.delete(id);
				}
			}
			selectedIds = next;
			notify();
		},
		clear() {
			selectedIds = new Set();
			notify();
		},
	};
}

export const DiscoverGamesBulkSelectContext = createContext<DiscoverGamesBulkSelectStore | null>(null);

export function useDiscoverGamesBulkSelectStore(): DiscoverGamesBulkSelectStore {
	const store = useContext(DiscoverGamesBulkSelectContext);
	if (!store) {
		throw new Error("useDiscoverGamesBulkSelect must be used within DiscoverGamesBulkSelectProvider");
	}
	return store;
}
